/**
 * Admin-editable email templates.
 * List on the left, editor + preview on the right. Mustache-style {{variable}}
 * substitution — a variable reference is listed underneath each template.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, RefreshCw, Save, Eye, PencilLine, Send } from "lucide-react";

import { useAuth, api } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";

const SAMPLE_VARS = {
    otp: { code: "824619", ttl_min: "10" },
    contract_sent: {
        contract_number: "NOXN-2026-0007",
        client_name_or_business: "Demo Client",
        business_name: "NOXN Demo Studio",
        run_window: "2026-03-01 → 2026-03-07",
        total_aud: "$2,750.00 AUD",
        portal_url: "https://noxn.com.au/portal/contract/example",
    },
    waitlist_promoted: {
        screen_name: "BNE Queen St LED",
        screen_city: "Brisbane",
        run_window: "2026-03-01 → 2026-03-07",
        client_name_or_business: "Demo Client",
        contract_ref: "Contract NOXN-2026-0007",
        portal_button_html: '<div style="text-align:center;margin:24px 0 8px;"><a href="#" style="display:inline-block;background:#FF5A00;color:#FFFFFF;text-decoration:none;padding:12px 24px;font-size:12px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;">Open my portal</a></div>',
    },
    campaign_summary: {
        client_name_or_business: "Demo Client",
        launch_date: "2026-03-01",
        total_aud: "$2,750.00 AUD",
    },
};

const substitute = (tpl, values, escapeValues) => {
    if (!tpl) return "";
    return tpl.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, name) => {
        const raw = values[name] ?? "";
        if (!escapeValues || name.endsWith("_html")) return String(raw);
        return String(raw)
            .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    });
};

export default function EmailTemplates() {
    usePageMeta({ title: "Email templates · NOXN", description: "Edit the automated emails NOXN sends." });
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedKey, setSelectedKey] = useState(null);
    const [draft, setDraft] = useState({ subject: "", html: "" });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/email-templates");
            setTemplates(data);
            if (!selectedKey && data.length) setSelectedKey(data[0].key);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load templates.");
        } finally { setLoading(false); }
    }, [selectedKey]);

    useEffect(() => { load(); }, [load]);

    const selected = useMemo(() => templates.find((t) => t.key === selectedKey), [templates, selectedKey]);
    useEffect(() => {
        if (selected) setDraft({ subject: selected.subject, html: selected.html });
    }, [selected?.key, selected?.subject, selected?.html]);

    const sampleVars = SAMPLE_VARS[selectedKey] || {};
    const previewSubject = substitute(draft.subject, sampleVars, false);
    const previewHtml = substitute(draft.html, sampleVars, true);

    const save = async () => {
        if (!selected) return;
        setBusy(true);
        try {
            await api.patch(`/email-templates/${selected.key}`, {
                subject: draft.subject, html: draft.html,
            });
            toast.success("Template saved.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally { setBusy(false); }
    };

    const reset = async () => {
        if (!selected) return;
        if (!window.confirm(`Reset "${selected.name}" to the NOXN default?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/email-templates/${selected.key}/reset`);
            setDraft({ subject: data.subject, html: data.html });
            toast.success("Template reset to default.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed.");
        } finally { setBusy(false); }
    };

    const sendTest = async () => {
        if (!selected) return;
        // If unsaved edits exist, save them first so the send-test uses the current draft.
        const hasUnsavedEdits = draft.subject !== selected.subject || draft.html !== selected.html;
        setBusy(true);
        try {
            if (hasUnsavedEdits) {
                await api.patch(`/email-templates/${selected.key}`, {
                    subject: draft.subject, html: draft.html,
                });
            }
            const { data } = await api.post(`/email-templates/${selected.key}/send-test`, {});
            toast.success(`Test email sent to ${data.to}.`);
            if (hasUnsavedEdits) await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send-test failed.");
        } finally { setBusy(false); }
    };

    if (user && user.role === "client") {
        return (
            <div className="min-h-screen w-full bg-[#0F1E3B] px-6 py-24 text-center">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Admin only</div>
                <div className="mt-2 text-white text-lg">Email templates are managed by NOXN admins.</div>
                <Link to="/dashboard" className="mt-6 inline-block text-xs font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white">← Back to dashboard</Link>
            </div>
        );
    }

    return (
        <PortalShell title="Email templates" mainClassName="p-0">
        <div className="min-h-screen w-full relative bg-[#0F1E3B]">
            <div className="absolute inset-0 -z-10 grid-noise opacity-25" />

            <main className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
                <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, ease: [0.16,1,0.3,1] }}>
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-4 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" /> Settings · Email templates
                    </div>
                    <h1 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl leading-[0.95]">
                        Own the voice.
                    </h1>
                    <p className="mt-4 text-slate-400 text-sm max-w-xl">
                        Every automated NOXN email is editable. Use <code className="text-[#FF5A00]">{"{{variable}}"}</code> to insert data — variables are listed below the editor.
                    </p>
                </motion.div>

                {loading ? (
                    <div className="mt-16 flex items-center justify-center"><Loader2 size={22} className="animate-spin text-[#FF5A00]" /></div>
                ) : (
                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
                        <aside className="space-y-1" data-testid="emails-template-list">
                            {templates.map((t) => (
                                <button key={t.key}
                                    onClick={() => setSelectedKey(t.key)}
                                    data-testid={`emails-template-item-${t.key}`}
                                    className={`w-full text-left px-4 py-3 border transition-colors ${
                                        selectedKey === t.key
                                            ? "border-[#FF5A00] bg-[#FF5A00]/10 text-white"
                                            : "border-white/10 hover:border-white/30 text-slate-300"
                                    }`}>
                                    <div className="text-sm font-medium truncate">{t.name}</div>
                                    <div className="mt-0.5 text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 truncate">
                                        {t.key}{!t.is_default ? " · edited" : ""}
                                    </div>
                                </button>
                            ))}
                        </aside>

                        {selected && (
                            <section className="space-y-4" data-testid="emails-editor">
                                <div className="border border-white/10 bg-[#1B2E55]/60 p-5 space-y-4">
                                    <div>
                                        <label className="block text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-1.5">Subject</label>
                                        <input type="text"
                                            data-testid="emails-editor-subject"
                                            value={draft.subject}
                                            onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                                            className="w-full bg-[#0F1E3B] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00] outline-none" />
                                    </div>
                                    <div>
                                        <label className="flex items-center gap-1 text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-1.5">
                                            <PencilLine size={11} /> HTML body
                                        </label>
                                        <textarea
                                            data-testid="emails-editor-html"
                                            value={draft.html}
                                            onChange={(e) => setDraft((d) => ({ ...d, html: e.target.value }))}
                                            spellCheck={false}
                                            className="w-full h-72 bg-[#0F1E3B] border border-white/10 text-slate-200 px-3 py-2.5 text-xs font-mono-tech leading-relaxed focus:border-[#FF5A00] outline-none" />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button onClick={save} disabled={busy}
                                            data-testid="emails-editor-save"
                                            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-60 text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">
                                            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                                        </button>
                                        <button onClick={reset} disabled={busy}
                                            data-testid="emails-editor-reset"
                                            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-slate-300 hover:text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest disabled:opacity-40">
                                            <RefreshCw size={12} /> Reset to default
                                        </button>
                                        <button onClick={sendTest} disabled={busy}
                                            data-testid="emails-editor-send-test"
                                            title={`Send a test with SAMPLE_VARS to ${user?.email || "your inbox"}`}
                                            className="inline-flex items-center gap-2 border border-[#FF5A00]/40 hover:border-[#FF5A00] hover:bg-[#FF5A00] text-[#FF5A00] hover:text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest disabled:opacity-40 transition-colors">
                                            <Send size={12} /> Send test
                                        </button>
                                        {selected.updated_at && (
                                            <span className="ml-auto text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">
                                                Updated {new Date(selected.updated_at).toLocaleString("en-AU")}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-2">/ Variables</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {(selected.variables || []).map((v) => (
                                            <div key={v.name} className="border border-white/10 p-3">
                                                <code className="text-[#FF5A00] text-xs font-mono-tech">{"{{" + v.name + "}}"}</code>
                                                <div className="text-xs text-slate-400 mt-1">{v.description}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="border border-white/10 bg-white">
                                    <div className="flex items-center justify-between px-4 py-2 bg-[#0F1E3B] text-white border-b border-white/10">
                                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] flex items-center gap-1.5">
                                            <Eye size={11} /> Live preview
                                        </div>
                                        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 truncate max-w-[70%]" data-testid="emails-preview-subject">
                                            {previewSubject}
                                        </div>
                                    </div>
                                    <iframe title="Email preview" srcDoc={previewHtml}
                                        data-testid="emails-preview-iframe"
                                        className="w-full h-[520px] bg-white" sandbox="" />
                                </div>
                            </section>
                        )}
                    </div>
                )}
            </main>
        </div>
        </PortalShell>
    );
}
